/**
 * BuildInspector — Phase 5 View 4.
 *
 * Sources:
 *   1. Phase 3 `/versions/{id}/inspect` — version metadata + stage_outcomes
 *      + build_provenance (single ingest_run_id, fanout_job_ids[]).
 *   2. Phase 4 `/concepts/extraction-status` — per-creative status with
 *      inline list of creative records (creative_id, status, concept_count,
 *      binding_count). The list is client-scoped only.
 *   3. Phase 4 `/fanout/backfill` — kicks a backfill batch and returns
 *      processed/created/error counts plus a batch_id.
 *
 * URL: /admin/ontology/:clientUid/builds?version=
 */
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Workflow,
  Loader2,
  Database,
  Zap,
  GitBranch,
  RefreshCw,
} from 'lucide-react';
import { clsx } from 'clsx';
import {
  pdoLifecycleApi,
  gardenApi,
  type PdoVersionInspectResponse,
  type PdoStageOutcome,
  type ExtractionStatusCreative,
} from '@/services/api';
import { useAppStore } from '@/stores/app';
import { StageStatusIcon } from '@/components/pdo/StatusBadges';
import { EmptyState } from '@/components/pdo/EmptyState';
import { ErrorBanner } from '@/components/pdo/ErrorBanner';
import {
  useResolvedClientUid,
  useFetch,
  formatDuration,
  formatNumber,
} from '@/components/pdo/utils';

export function BuildInspector() {
  const clientUid = useResolvedClientUid('/admin/ontology');
  const [searchParams, setSearchParams] = useSearchParams();

  const versionsQuery = useFetch(
    () =>
      clientUid
        ? pdoLifecycleApi.listVersions(clientUid, { limit: 50 })
        : Promise.resolve({ versions: [], next_cursor: null }),
    [clientUid],
  );
  const versions = versionsQuery.data?.versions ?? [];
  const currentVersionId =
    versions.find((v) => v.status === 'current')?.version_id ??
    versions[0]?.version_id ??
    null;
  const selectedVersion = searchParams.get('version') ?? currentVersionId;

  const detail = useFetch(
    () =>
      clientUid && selectedVersion
        ? pdoLifecycleApi.inspectVersion(clientUid, selectedVersion)
        : Promise.resolve(null as unknown as PdoVersionInspectResponse),
    [clientUid, selectedVersion],
  );

  if (!clientUid) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-slate-500">
        <Loader2 size={16} className="animate-spin mr-2" /> Resolving client…
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full space-y-5">
      <header className="flex items-start justify-between gap-4 px-1 pb-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-teal to-brand-green flex items-center justify-center text-navy-900 shadow-glow">
            <Workflow size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-navy-900">Build Inspector</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Trace lineage and per-stage outcomes for{' '}
              <span className="font-mono text-navy-900">{clientUid}</span>.
            </p>
          </div>
        </div>
        <div>
          <select
            value={selectedVersion ?? ''}
            onChange={(e) =>
              setSearchParams(
                (prev) => {
                  const next = new URLSearchParams(prev);
                  next.set('version', e.target.value);
                  return next;
                },
                { replace: true },
              )
            }
            className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-white"
          >
            {versions.map((v) => (
              <option key={v.version_id} value={v.version_id}>
                {v.version_id} {v.status === 'current' ? '(current)' : ''}
              </option>
            ))}
            {versions.length === 0 && <option value="">No versions</option>}
          </select>
        </div>
      </header>

      {detail.error && <ErrorBanner error={detail.error} onRetry={detail.refetch} />}

      {detail.loading && !detail.data ? (
        <div className="flex items-center justify-center py-16 text-xs text-slate-400">
          <Loader2 size={14} className="animate-spin mr-2" /> Loading build…
        </div>
      ) : detail.data ? (
        <>
          <LineageDiagram detail={detail.data} />
          <StageMetricsTable stage_outcomes={detail.data.stage_outcomes} />
        </>
      ) : (
        <EmptyState
          title="No build selected"
          hint="Pick a PDO version above to inspect its build lineage."
          icon={<Workflow size={18} />}
        />
      )}

      <ExtractionDashboard clientUid={clientUid} />
    </div>
  );
}

// ─── Lineage diagram (SVG) ──────────────────────────────────────────────────

function LineageDiagram({ detail }: { detail: PdoVersionInspectResponse }) {
  const ingest = detail.build_provenance.ingest_run_id; // single id
  const fanout = detail.build_provenance.fanout_job_ids; // []
  const fanoutY = (i: number) => 40 + i * 50;

  const height = Math.max(fanout.length * 50 + 60, 180);

  const xIngest = 80;
  const xFanout = 320;
  const xVersion = 560;
  const versionY = height / 2;

  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
      <h3 className="text-sm font-semibold text-navy-900 mb-3">Build lineage</h3>
      {!ingest && fanout.length === 0 ? (
        <EmptyState
          compact
          title="No lineage recorded"
          hint="This build was created before lineage tracking was enabled."
        />
      ) : (
        <svg width="100%" viewBox={`0 0 680 ${height}`} className="overflow-visible">
          {/* ingest → fanout edges */}
          {ingest &&
            fanout.map((id, i) => (
              <line
                key={`il-${id}`}
                x1={xIngest + 60}
                y1={versionY}
                x2={xFanout - 60}
                y2={fanoutY(i)}
                stroke="#cbd5e1"
                strokeWidth={1.5}
              />
            ))}
          {/* fanout → version edges */}
          {fanout.map((id, i) => (
            <line
              key={`fl-${id}`}
              x1={xFanout + 60}
              y1={fanoutY(i)}
              x2={xVersion - 60}
              y2={versionY}
              stroke="#cbd5e1"
              strokeWidth={1.5}
            />
          ))}
          {ingest ? (
            <LineageNode
              x={xIngest}
              y={versionY}
              tier="Ingest"
              color="#60a5fa"
              title={ingest}
              subtitle="ingest run"
            />
          ) : (
            <PlaceholderNode x={xIngest} y={versionY} label="No ingest" />
          )}
          {fanout.length === 0 ? (
            <PlaceholderNode x={xFanout} y={versionY} label="No fanout" />
          ) : (
            fanout.map((id, i) => (
              <LineageNode
                key={id}
                x={xFanout}
                y={fanoutY(i)}
                tier="Fanout"
                color="#a78bfa"
                title={id}
                subtitle="fanout job"
              />
            ))
          )}
          <LineageNode
            x={xVersion}
            y={versionY}
            tier="PDO"
            color="#10b981"
            title={detail.version_id}
            subtitle={`${formatNumber(detail.metrics.concept_count)} concepts · ${formatNumber(
              detail.metrics.edge_count,
            )} edges`}
            big
          />
        </svg>
      )}
      <div className="grid grid-cols-3 gap-3 mt-4 text-xs">
        <LegendCell
          color="#60a5fa"
          icon={<Database size={11} />}
          label="IngestRun"
          hint={ingest ?? 'none'}
        />
        <LegendCell
          color="#a78bfa"
          icon={<Zap size={11} />}
          label="FanoutJob"
          hint={`${fanout.length} job(s)`}
        />
        <LegendCell
          color="#10b981"
          icon={<GitBranch size={11} />}
          label="PDOVersion"
          hint={detail.version_id}
        />
      </div>
      {detail.build_provenance.graph_build_id && (
        <p className="mt-3 text-[11px] text-slate-500 font-mono">
          graph_build_id: {detail.build_provenance.graph_build_id}
        </p>
      )}
      {detail.build_provenance.bootstrap_job_id && (
        <p className="mt-1 text-[11px] text-slate-500 font-mono">
          bootstrap_job_id: {detail.build_provenance.bootstrap_job_id}
        </p>
      )}
    </section>
  );
}

function LineageNode({
  x,
  y,
  tier,
  color,
  title,
  subtitle,
  big = false,
}: {
  x: number;
  y: number;
  tier: string;
  color: string;
  title: string;
  subtitle: string;
  big?: boolean;
}) {
  const w = big ? 140 : 120;
  const h = big ? 44 : 36;
  return (
    <g transform={`translate(${x - w / 2} ${y - h / 2})`}>
      <rect width={w} height={h} rx={6} ry={6} fill="#ffffff" stroke={color} strokeWidth={2} />
      <rect width={5} height={h} fill={color} />
      <text
        x={14}
        y={14}
        fontSize={9}
        fontFamily="JetBrains Mono, Consolas, monospace"
        fill="#64748b"
      >
        {tier}
      </text>
      <text
        x={14}
        y={26}
        fontSize={11}
        fontWeight={600}
        fontFamily="Inter, system-ui, sans-serif"
        fill="#0f172a"
      >
        {title.length > 16 ? `${title.slice(0, 14)}…` : title}
      </text>
      {big && (
        <text
          x={14}
          y={38}
          fontSize={9}
          fontFamily="JetBrains Mono, Consolas, monospace"
          fill="#64748b"
        >
          {subtitle.length > 22 ? `${subtitle.slice(0, 20)}…` : subtitle}
        </text>
      )}
      <title>{`${tier}: ${title}\n${subtitle}`}</title>
    </g>
  );
}

function PlaceholderNode({
  x,
  y,
  label,
}: {
  x: number;
  y: number;
  label: string;
}) {
  return (
    <g transform={`translate(${x - 50} ${y - 14})`}>
      <rect
        width={100}
        height={28}
        rx={6}
        ry={6}
        fill="#f8fafc"
        stroke="#e2e8f0"
        strokeDasharray="4 3"
      />
      <text
        x={50}
        y={18}
        fontSize={10}
        textAnchor="middle"
        fill="#94a3b8"
        fontFamily="Inter, system-ui, sans-serif"
      >
        {label}
      </text>
    </g>
  );
}

function LegendCell({
  color,
  icon,
  label,
  hint,
}: {
  color: string;
  icon: React.ReactNode;
  label: string;
  hint: string;
}) {
  return (
    <div className="flex items-center gap-2 px-2.5 py-1.5 rounded border border-slate-200 bg-slate-50">
      <span
        className="w-5 h-5 rounded flex items-center justify-center text-white"
        style={{ backgroundColor: color }}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold text-navy-900">{label}</p>
        <p className="text-[10px] font-mono text-slate-500 truncate">{hint}</p>
      </div>
    </div>
  );
}

// ─── Stage metrics table ────────────────────────────────────────────────────

function StageMetricsTable({
  stage_outcomes,
}: {
  stage_outcomes: Record<string, PdoStageOutcome>;
}) {
  const entries = Object.entries(stage_outcomes ?? {});
  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <header className="px-4 py-2 border-b border-slate-200 bg-slate-50">
        <h3 className="text-sm font-semibold text-navy-900">Stage metrics</h3>
      </header>
      {entries.length === 0 ? (
        <EmptyState compact title="No stage outcomes recorded" />
      ) : (
        <ul className="divide-y divide-slate-100">
          {entries.map(([name, oc]) => (
            <StageRow key={name} name={name} stage={oc} />
          ))}
        </ul>
      )}
    </section>
  );
}

function StageRow({ name, stage }: { name: string; stage: PdoStageOutcome }) {
  const [open, setOpen] = useState(false);
  const elapsedMs =
    stage.started_at && stage.finished_at
      ? new Date(stage.finished_at).getTime() - new Date(stage.started_at).getTime()
      : null;
  const summaryEntries = Object.entries(stage.summary ?? {}).filter(
    ([, v]) => v != null && (typeof v !== 'object' || Array.isArray(v)),
  );
  const errors = stage.errors ?? [];

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left"
      >
        <StageStatusIcon status={stage.status} />
        <span className="text-sm font-medium text-navy-900 w-40 truncate">{name}</span>
        <span className="text-xs font-mono text-slate-500 w-20">
          {elapsedMs != null ? formatDuration(elapsedMs) : '–'}
        </span>
        <div className="flex-1 flex items-center gap-3 text-xs text-slate-600 truncate">
          {summaryEntries.slice(0, 3).map(([k, v]) => (
            <span key={k}>
              <span className="text-slate-500">{k}:</span>{' '}
              <span className="font-mono text-slate-800">
                {Array.isArray(v) ? `${v.length}` : String(v)}
              </span>
            </span>
          ))}
          {summaryEntries.length > 3 && (
            <span className="text-slate-400 text-[11px]">
              +{summaryEntries.length - 3} more
            </span>
          )}
        </div>
        <span className="text-[10px] uppercase tracking-wider text-slate-400">
          {open ? 'Hide' : 'Detail'}
        </span>
      </button>
      {open && (
        <div className="bg-slate-50 px-4 py-3 border-t border-slate-100">
          {summaryEntries.length > 0 && (
            <div className="grid grid-cols-3 gap-x-6 gap-y-1 text-xs mb-2">
              {summaryEntries.map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="text-slate-500">{k}</span>
                  <span className="font-mono text-slate-800">
                    {Array.isArray(v) ? `${v.length} items` : String(v)}
                  </span>
                </div>
              ))}
            </div>
          )}
          {errors.length > 0 ? (
            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-red-600">
                Errors
              </p>
              {errors.map((e, i) => (
                <p key={i} className="text-red-600 font-mono text-[11px]">
                  {String(e.message ?? JSON.stringify(e))}
                </p>
              ))}
            </div>
          ) : (
            summaryEntries.length === 0 && (
              <p className="text-xs text-slate-400 italic">No summary recorded</p>
            )
          )}
        </div>
      )}
    </li>
  );
}

// ─── Extraction dashboard ───────────────────────────────────────────────────

function ExtractionDashboard({ clientUid }: { clientUid: string }) {
  const status = useFetch(() => gardenApi.extractionStatus(clientUid), [clientUid]);
  const addNotification = useAppStore((s) => s.addNotification);
  const [filter, setFilter] = useState<'all' | 'pending' | 'failed'>('all');
  const [busy, setBusy] = useState(false);

  const counts = status.data?.counts ?? {
    pending: 0,
    running: 0,
    complete: 0,
    failed: 0,
  };
  const creatives = status.data?.creatives ?? [];

  const visibleCreatives = useMemo(() => {
    if (filter === 'all') return creatives;
    return creatives.filter((c) => c.status === filter);
  }, [creatives, filter]);

  const runBackfill = async () => {
    setBusy(true);
    try {
      const res = await gardenApi.fanoutBackfill(clientUid);
      addNotification(
        'success',
        `Backfill ${res.batch_id} processed ${res.total_processed} · created ${res.concepts_created} concepts, ${res.bindings_created} bindings`,
      );
      status.refetch();
    } catch (e) {
      addNotification('error', e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <header className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 bg-slate-50">
        <Database size={14} className="text-teal-600" />
        <h3 className="text-sm font-semibold text-navy-900">Concept extraction</h3>
        <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-500">
          <button
            type="button"
            onClick={status.refetch}
            className="text-slate-400 hover:text-slate-600"
            title="Refresh"
          >
            <RefreshCw size={12} className={clsx(status.loading && 'animate-spin')} />
          </button>
          <button
            type="button"
            onClick={runBackfill}
            disabled={busy}
            className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium border border-teal-200 text-teal-700 rounded hover:bg-teal-50 disabled:opacity-40"
          >
            {busy ? (
              <Loader2 size={10} className="animate-spin" />
            ) : (
              <RefreshCw size={10} />
            )}
            Run Backfill
          </button>
        </span>
      </header>

      {status.error && <ErrorBanner error={status.error} onRetry={status.refetch} />}

      <div className="grid grid-cols-4 gap-2 p-3">
        <CountTile label="Complete" value={counts.complete} tone="emerald" />
        <CountTile label="Running" value={counts.running} tone="amber" />
        <CountTile label="Pending" value={counts.pending} tone="slate" />
        <CountTile label="Failed" value={counts.failed} tone="red" />
      </div>

      <div className="px-3 pb-2 flex items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
          Filter:
        </span>
        {(['all', 'pending', 'failed'] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={clsx(
              'px-2 py-0.5 text-[10px] font-medium rounded border capitalize',
              filter === f
                ? 'bg-teal-100 border-teal-200 text-teal-700'
                : 'bg-white border-slate-200 text-slate-600',
            )}
          >
            {f}
          </button>
        ))}
      </div>

      {status.loading && creatives.length === 0 ? (
        <div className="flex items-center justify-center py-8 text-xs text-slate-400">
          <Loader2 size={12} className="animate-spin mr-2" /> Loading…
        </div>
      ) : visibleCreatives.length === 0 ? (
        <EmptyState
          compact
          title={
            filter === 'all'
              ? 'No creatives recorded'
              : `No ${filter} creatives`
          }
        />
      ) : (
        <ul className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
          {visibleCreatives.map((c) => (
            <CreativeRow key={c.creative_id} creative={c} />
          ))}
        </ul>
      )}
    </section>
  );
}

function CountTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'emerald' | 'amber' | 'red' | 'slate';
}) {
  return (
    <div
      className={clsx(
        'px-3 py-2 rounded-lg border',
        tone === 'emerald' && 'border-emerald-200 bg-emerald-50',
        tone === 'amber' && 'border-amber-200 bg-amber-50',
        tone === 'red' && 'border-red-200 bg-red-50',
        tone === 'slate' && 'border-slate-200 bg-slate-50',
      )}
    >
      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">
        {label}
      </p>
      <p
        className={clsx(
          'text-xl font-mono font-semibold mt-0.5',
          tone === 'emerald' && 'text-emerald-700',
          tone === 'amber' && 'text-amber-700',
          tone === 'red' && 'text-red-700',
          tone === 'slate' && 'text-slate-700',
        )}
      >
        {formatNumber(value)}
      </p>
    </div>
  );
}

function CreativeRow({ creative }: { creative: ExtractionStatusCreative }) {
  return (
    <li className="px-4 py-2 flex items-center gap-3 text-xs hover:bg-slate-50">
      <span
        className={clsx(
          'inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-medium capitalize',
          creative.status === 'complete' &&
            'bg-emerald-50 border-emerald-200 text-emerald-700',
          creative.status === 'pending' &&
            'bg-slate-50 border-slate-200 text-slate-700',
          creative.status === 'running' &&
            'bg-amber-50 border-amber-200 text-amber-700',
          creative.status === 'failed' &&
            'bg-red-50 border-red-200 text-red-700',
        )}
      >
        {creative.status}
      </span>
      <span className="font-mono text-navy-900 flex-1 truncate">
        {creative.creative_id}
      </span>
      <span className="text-slate-500">
        concepts{' '}
        <span className="font-mono text-slate-800">{creative.concept_count}</span>
      </span>
      <span className="text-slate-500">
        bindings{' '}
        <span className="font-mono text-slate-800">{creative.binding_count}</span>
      </span>
    </li>
  );
}
